import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function HowItWorks() {
  const { t } = useTranslation();

  const steps = [
    {
      icon: 'ri-qr-code-line',
      title: t('howItWorks.step1.title'),
      description: t('howItWorks.step1.desc'),
      demo: 'https://public.readdy.ai/ai/img_res/4bf37e14ded908636b4201a6ed2f113b.jpg',
    },
    {
      icon: 'ri-link',
      title: t('howItWorks.step2.title'),
      description: t('howItWorks.step2.desc'),
      demo: 'https://readdy.ai/api/search-image?query=Clean%20illustration%20showing%20a%20long%20URL%20transforming%20into%20a%20short%20link%20with%20arrow%20animation%2C%20modern%20flat%20design%20with%20teal%20and%20white%20colors%20on%20light%20background%2C%20minimalist%20style&width=600&height=400&seq=link-demo&orientation=landscape',
    },
    {
      icon: 'ri-user-line',
      title: t('howItWorks.step3.title'),
      description: t('howItWorks.step3.desc'),
      demo: 'https://readdy.ai/api/search-image?query=Elegant%20illustration%20of%20a%20mobile%20phone%20displaying%20a%20bio%20link%20page%20with%20multiple%20social%20media%20buttons%2C%20modern%20gradient%20background%20with%20teal%20accents%2C%20clean%20and%20professional%20design&width=600&height=400&seq=bio-demo&orientation=landscape',
    },
  ];

  const features = [
    {
      icon: 'ri-bar-chart-line',
      title: t('howItWorks.feature1.title'),
      description: t('howItWorks.feature1.desc'),
    },
    {
      icon: 'ri-smartphone-line',
      title: t('howItWorks.feature2.title'),
      description: t('howItWorks.feature2.desc'),
    },
    {
      icon: 'ri-shield-check-line',
      title: t('howItWorks.feature3.title'),
      description: t('howItWorks.feature3.desc'),
    },
    {
      icon: 'ri-palette-line',
      title: t('howItWorks.feature4.title'),
      description: t('howItWorks.feature4.desc'),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50">
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-qr-code-line text-white text-lg"></i>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent">LinkQR</span>
            </Link>
            <Link
              to="/login"
              className="px-6 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('common.getStarted')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            {t('howItWorks.title')}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {t('howItWorks.subtitle')}
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } items-center gap-12 mb-24 last:mb-0`}
            >
              <div className="flex-1">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <img
                    src={step.demo}
                    alt={step.title}
                    className="w-full h-80 object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <i className={`${step.icon} text-white text-3xl`}></i>
                  </div>
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-teal-600">{index + 1}</span>
                  </div>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-lg text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            {t('howItWorks.features.title')}
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16">
            {t('howItWorks.features.subtitle')}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-2xl hover:bg-gray-50 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className={`${feature.icon} text-white text-2xl`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-12 shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              {t('howItWorks.cta.title')}
            </h2>
            <p className="text-xl text-teal-50 mb-8">
              {t('howItWorks.cta.subtitle')}
            </p>
            <Link
              to="/"
              className="inline-block px-8 py-4 bg-white text-teal-600 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('howItWorks.cta.button')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
              <i className="ri-qr-code-line text-white text-lg"></i>
            </div>
            <span className="text-xl font-bold">LinkQR</span>
          </div>
          <p className="text-gray-400 mb-6">
            {t('howItWorks.footer.desc')}
          </p>
          <div className="flex justify-center gap-6 mb-8">
            <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
              <i className="ri-twitter-fill text-2xl"></i>
            </a>
            <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
              <i className="ri-facebook-fill text-2xl"></i>
            </a>
            <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
              <i className="ri-instagram-line text-2xl"></i>
            </a>
          </div>
          <div className="border-t border-gray-800 pt-6">
            <p className="text-gray-500 text-sm">
              © 2024 LinkQR. {t('footer.rights')} •{' '}
              <a href="https://readdy.ai/?origin=logo" className="hover:text-teal-400 transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
